import mitt from "mitt";

import { ITeamDeviceDB } from "../../interfaces/Team";

import { getDeviceStream } from "../../Utils/mediaUser";

interface MicrophoneContext {
  stream: MediaStream;

  source: MediaStreamAudioSourceNode;

  gainNode: GainNode;

  analyser: AnalyserNode;
}

type MicrophoneEvents = {
  muted: { deviceId: string };

  unmuted: { deviceId: string };
};

class Microphones {
  private Devices = new Map<string, ITeamDeviceDB>();

  private Context = {
    Mics: new Map<string, MicrophoneContext>(),
  };

  private AudioContext!: AudioContext;

  private MediaStreamDestination!: MediaStreamAudioDestinationNode;

  private emitter = mitt<MicrophoneEvents>();

  // Generación por-device para resolver la carrera play/stop/destroy sobre el
  // MISMO micrófono mientras `getUserMedia` está pendiente. `play()` toma un
  // token antes del await; si un stop()/destroy()/play() más nuevo corre durante
  // ese await, la generación cambia y la invocación vieja descarta el stream que
  // acaba de abrir en vez de activarlo (si no, quedaba ON contra la voluntad del
  // usuario, o dos streams sonando a la vez con el primero sin cerrar).
  private PlayGen = new Map<string, number>();

  private Destroyed = false;

  constructor(
    audioContext?: AudioContext,

    destination?: MediaStreamAudioDestinationNode,
  ) {
    this.createInstance(audioContext, destination);
  }

  createInstance(
    audioContext?: AudioContext,

    destination?: MediaStreamAudioDestinationNode,
  ) {
    try {
      if (!this.AudioContext && audioContext && destination) {
        this.AudioContext = audioContext;

        this.MediaStreamDestination = destination;
      }
    } catch (error) {
      console.error(error);
    }
  }

  getAll() {
    return Array.from(this.Devices.values());
  }

  get(deviceId: string) {
    return this.Devices.get(deviceId);
  }

  set(deviceId: string, device: ITeamDeviceDB) {
    this.Devices.set(deviceId, device);
  }

  setVolume(deviceId: string, newVolume: number) {
    const mic = this.Context.Mics.get(deviceId);

    if (!mic) return;

    mic.gainNode.gain.value = newVolume;
  }

  delete(deviceId: string) {
    this.Devices.delete(deviceId);
  }

  async stop(deviceId: string) {
    // Invalidar cualquier play() in-flight para este device: si el stop llega
    // mientras getUserMedia sigue pendiente, el play descarta su stream al
    // resolver en vez de dejar el mic prendido.
    this.PlayGen.set(deviceId, (this.PlayGen.get(deviceId) ?? 0) + 1);

    const mic = this.Context.Mics.get(deviceId);

    if (!mic) return;

    mic.stream
      .getAudioTracks()
      .forEach((track: MediaStreamTrack) => track.stop());

    this.disconnectNodes(mic);

    this.Context.Mics.delete(deviceId);

    this.emitter.emit("muted", { deviceId });
  }

  // Parar el track NO desconecta los AudioNodes: sin esto cada ciclo on/off deja
  // el analyser (y su cadena source→gain→analyser) colgado en el
  // MediaStreamDestination del broadcast → fuga de nodos. El watchGraph se corta
  // solo por el check de arriba (mic ya no activo).
  private disconnectNodes(mic: MicrophoneContext) {
    try {
      mic.source.disconnect();
    } catch {
      /* nodo ya desconectado / contexto cerrado */
    }

    try {
      mic.gainNode.disconnect();
    } catch {
      /* idem */
    }

    try {
      mic.analyser.disconnect();
    } catch {
      /* idem */
    }
  }

  async play(deviceId: string, settings?: ITeamDeviceDB["capabilities"]) {
    // Instancia ya destruida: no abrir el device del SO (resume + getUserMedia)
    // solo para descartar el stream después.
    if (this.Destroyed) return;

    // Idempotente: si el mic ya está activo, no abrir un segundo stream (un
    // doble play — re-render / trigger — duplicaba el audio y fugaba el 1er
    // stream, dejando el indicador de mic del SO prendido).
    if (this.Context.Mics.has(deviceId)) return;

    const mic = this.Devices.get(deviceId);

    if (!mic) return;

    // Token de esta invocación, ANTES de cualquier await. Un stop()/destroy()/
    // play() nuevo para el mismo device durante el await lo bumpea → esta
    // invocación queda invalidada. El contador NUNCA se borra (queda monótono):
    // si se reiniciara a 0, un stop() posterior podría re-acuñar el token de un
    // play() viejo aún pendiente (ABA) y re-activar el mic contra el stop. La
    // idempotencia la da el early-return por Context.Mics.has de arriba; el mapa
    // está acotado por la cantidad de devices.
    const gen = (this.PlayGen.get(deviceId) ?? 0) + 1;

    this.PlayGen.set(deviceId, gen);

    if (this.AudioContext.state === "suspended") {
      try {
        await this.AudioContext.resume();
      } catch {
        // El contexto puede cerrarse durante un teardown concurrente
        // (Radio.destroy cierra el AudioContext). No propagar: play() es
        // fire-and-forget → un throw sería un unhandled rejection.
        return;
      }

      // Un stop()/destroy() durante el resume ya invalidó esta invocación: no
      // abrir el device (evita prender el mic del SO tras un stop ya emitido).
      if (this.Destroyed || this.PlayGen.get(deviceId) !== gen) return;
    }

    let stream: MediaStream;

    try {
      stream = await getDeviceStream(
        mic.deviceInfo as unknown as MediaDeviceInfo,

        true,

        false,

        // Sin defaults: lo no seteado viaja como undefined y getDeviceStream lo
        // omite del constraint — el browser aplica los suyos, como hasta ahora.
        {
          sampleRate: settings?.sampleRate,

          channelCount: settings?.channelCount,

          echoCancellation: settings?.echoCancellation,

          noiseSuppression: settings?.noiseSuppression,

          autoGainControl: settings?.autoGainControl,
        },
      );
    } catch (error) {
      // getUserMedia rechazado (permiso denegado, device desenchufado). No
      // propagar: el caller de la UI llama a play() fire-and-forget → un throw
      // sería un unhandled rejection. No tocamos el token (queda monótono).
      console.error("[Microphones] no se pudo abrir el micrófono:", error);

      return;
    }

    // ¿Nos invalidaron durante el await? (stop/destroy, o un play más nuevo).
    // Descartar el stream recién abierto para no fugarlo ni activar un mic que
    // ya no corresponde.
    if (
      this.Destroyed ||
      this.PlayGen.get(deviceId) !== gen ||
      this.Context.Mics.has(deviceId)
    ) {
      stream.getAudioTracks().forEach((track) => track.stop());

      return;
    }

    const source = this.AudioContext.createMediaStreamSource(stream);

    const gainNode = this.AudioContext.createGain();

    gainNode.gain.value = 1.0;

    source.connect(gainNode);

    const analyser = this.AudioContext.createAnalyser();

    analyser.fftSize = 512;

    analyser.smoothingTimeConstant = 0.3;

    gainNode.connect(analyser);

    analyser.connect(this.MediaStreamDestination);

    this.Context.Mics.set(deviceId, { stream, source, gainNode, analyser });

    this.emitter.emit("unmuted", { deviceId });

    setTimeout(() => {
      const canvas = document.getElementById(deviceId) as HTMLCanvasElement;

      const canvasCtx = canvas?.getContext("2d");

      if (canvas && canvasCtx) {
        this.watchGraph(deviceId, analyser, canvas, canvasCtx);
      }
    }, 200);
  }

  watchGraph(
    deviceId: string,

    analyser: AnalyserNode,

    canvas: HTMLCanvasElement,

    ctx: CanvasRenderingContext2D,
  ) {
    try {
      // Terminación del loop: cortar si este mic ya no está activo (stop/destroy
      // borró su entrada) o si un re-play lo reemplazó por otro analyser. Sin
      // esto el rAF se reprograma para siempre con un analyser colgado (fuga de
      // rAF + nodo vivo en el mix de broadcast).
      const current = this.Context.Mics.get(deviceId);

      if (!current || current.analyser !== analyser) return;

      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        setTimeout(() => this.watchGraph(deviceId, analyser, canvas, ctx), 100);

        return;
      }

      const ref = window.requestAnimationFrame(() =>
        this.watchGraph(deviceId, analyser, canvas, ctx),
      );

      const bufferLength = analyser.frequencyBinCount;

      const dataArray = new Uint8Array(bufferLength);

      analyser.getByteTimeDomainData(dataArray);

      ctx.fillStyle = "rgba(0, 0, 0, 0)";

      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.lineWidth = 2;

      ctx.strokeStyle = "#3b82f6";

      ctx.beginPath();

      const sliceWidth = canvas.width / bufferLength;

      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;

        const y = (v * canvas.height) / 2;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }

        x += sliceWidth;
      }

      ctx.stroke();

      return ref;
    } catch (error) {
      console.log("Error en watchGraph:", error);

      return false;
    }
  }

  on<K extends keyof MicrophoneEvents>(
    type: K,

    handler: (payload: MicrophoneEvents[K]) => void,
  ) {
    this.emitter.on(type, handler);
  }

  off<K extends keyof MicrophoneEvents>(
    type: K,

    handler: (payload: MicrophoneEvents[K]) => void,
  ) {
    this.emitter.off(type, handler);
  }

  getAnalyser(deviceId?: string): AnalyserNode | null {
    if (deviceId) {
      const mic = this.Context.Mics.get(deviceId);

      return mic?.analyser ?? null;
    }

    // Return the first active microphone's analyser

    const firstMic = this.Context.Mics.values().next();

    if (firstMic.done) return null;

    return firstMic.value.analyser;
  }

  getStream() {
    return this.MediaStreamDestination.stream;
  }

  hasActiveMicrophones() {
    return this.Context.Mics.size > 0;
  }

  getActiveMicrophones() {
    return Array.from(this.Context.Mics.keys());
  }

  destroy() {
    // Invalida todo play() in-flight: si getUserMedia resuelve después del
    // destroy, la comprobación de `Destroyed` en play() descarta el stream en
    // vez de re-poblar `Context.Mics` sobre un contexto ya cerrado.
    this.Destroyed = true;

    this.Context.Mics.forEach((mic) => {
      mic.stream

        .getAudioTracks()

        .forEach((track: MediaStreamTrack) => track.stop());

      this.disconnectNodes(mic);
    });

    this.Context.Mics.clear();

    // Vaciar el estado auxiliar del teardown (seguro: `Destroyed` ya invalida
    // cualquier play in-flight, así que no re-abre el hoyo ABA).
    this.PlayGen.clear();

    const devices = this.getAll();

    devices.forEach((device) => {
      this.delete(device.deviceInfo.deviceId);
    });

    this.emitter.all.clear();
  }
}

export default Microphones;
